/**
 * Log a meeting, v2 (docs/design/meetings-v2, approved 18 Oct; ADR-002 Part III). Four ways to add the minutes:
 *
 *   Record live        the browser's standard speech service (lib/speech/session.ts) turns speech into text in an
 *                      editable transcript; at most 2:00 per meeting across "Record more" (MEETING_LIVE_MAX_SECONDS).
 *                      Our server never receives audio. Firefox has no recogniser: the tile is off and it says so.
 *   Upload a document  POST /meetings/document/read returns the text (nothing stored); the person edits it.
 *   Type or paste      the same editor.
 *   Link a recording   the v1 link-only body; nothing transcribed.
 *
 * Submit is POST /meetings (always a draft); the modal closes and the existing review drawer opens on the draft,
 * where the found tasks are confirmed. One text buffer is shared by live, document and typed, so switching keeps it.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  MEETING_LIVE_MAX_SECONDS,
  MINUTES_FILE_EXTENSIONS,
  MINUTES_FILE_MAX_BYTES,
  type CreateMeetingBody,
  type Meeting,
} from '@amplispot/contracts';
import { ApiFailure, call } from '../../api';
import { useSession } from '../../lib/session';
import { useClients } from '../../lib/data';
import { addDays } from '../../lib/work';
import { todayIso } from '../../lib/format';
import { WHEN_OPTS, markJustAddedMeeting, readMeetingDocument, useMeetingWrite, usePrivacy, useRequestKey } from '../../lib/meetings';
import { probe } from '../../lib/speech/probe';
import { appendWords, startLive, type LiveSession, type StopReason } from '../../lib/speech/session';
import { useMicLevel } from '../../lib/speech/level';
import { VENDOR_NOTICE } from '../../lib/speech/notice';
import { VendorNotice } from '../../lib/speech/VendorNotice';
import { Modal, errorText, useFail } from '../../ui/kit';
import { Icon, type IconName } from '../../ui/Icon';

type Way = 'live' | 'doc' | 'type' | 'link';
type Rec = 'idle' | 'starting' | 'live' | 'stopped';
type Doc =
  | { k: 'none' }
  | { k: 'bad'; file: File; msg: string; server: boolean }
  | { k: 'reading'; file: File; ctl: AbortController }
  | { k: 'ok'; file: File; filename: string; chars: number };

const CAP_S = MEETING_LIVE_MAX_SECONDS;
const CAP_MS = CAP_S * 1000;
const MB = 1024 * 1024;
const CAP_MB = Math.round(MINUTES_FILE_MAX_BYTES / MB);
const MIN_CHARS = 10;
/** The Hearing line shows only the newest words of the phrase still being heard, so it never runs off the card. */
const HEAR_MAX = 90;
const MAX_CHARS = 100_000;
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;
const SHOWN = ['client_id', 'held_on', 'title', 'attendees', 'minutes_text', 'recording_url', 'consent_confirmed', 'duration_s', 'document_filename'];

const WAYS: [Way, IconName, string, string, string | null][] = [
  ['live', 'mic', 'Record live', `Speak for up to ${CAP_S / 60} minutes. The words appear as you talk, and you fix them before you submit.`, 'Chrome, Edge or Safari'],
  ['doc', 'doc', 'Upload a document', `Minutes of the meeting as PDF, Word or Excel, up to ${CAP_MB} MB. You check the text before you submit.`, null],
  ['type', 'pen', 'Type or paste', 'Write the minutes here, or paste them from an email or notes.', null],
  ['link', 'link', 'Link a recording', 'A Zoom, Meet or Drive link to a recording kept elsewhere. Nothing is transcribed.', null],
];
const SEGL: Record<Way, string> = { live: 'Record live', doc: 'Upload a document', type: 'Type or paste', link: 'Link a recording' };
const SEGI: Record<Way, IconName> = { live: 'mic', doc: 'doc', type: 'pen', link: 'link' };
const TYPES = ['PDF', 'Word .docx', 'Excel .xlsx', 'CSV', '.txt', '.md'];
const KIND: Record<string, string> = { pdf: 'PDF', docx: 'Word', xlsx: 'Excel', csv: 'CSV', txt: 'Text', md: 'Markdown' };
const AUDIO = /^(mp3|m4a|wav|ogg|oga|opus|webm|aac|flac|amr|wma|mp4|mov)$/;

const extOf = (name: string) => (/\.([a-z0-9]+)$/i.exec(name)?.[1] ?? '').toLowerCase();
const sizeLabel = (b: number) => (b < MB ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / MB).toFixed(1)} MB`);
const kindLabel = (f: File) => {
  const e = extOf(f.name);
  return KIND[e] ?? (AUDIO.test(e) ? 'Audio' : e.toUpperCase() || 'File');
};

/**
 * The microphone-blocked note above the greyed recorder card. [Try again] calls record() and reopens the microphone
 * from outside the card, so the vendor notice sits beside it (I6), as it does beside [Record].
 */
export function MicBlockedNote({ onRetry, onUpload }: { onRetry: () => void; onUpload: () => void }) {
  return (
    <div className="note bad" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <Icon name="micoff" size={18} />
      <div>
        <b>Voice typing is not available in this browser. Type it instead.</b>
        <br />
        The microphone is blocked, or there isn’t one. To use voice, allow the microphone for this site from the browser’s address bar, then press Try again. You can
        also paste the minutes or upload them as a document.
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          <button className="btn sm" onClick={onRetry}>
            Try again
          </button>
          <button className="btn sm" onClick={onUpload}>
            <Icon name="doc" size={14} /> Upload a document
          </button>
        </div>
        <VendorNotice />
      </div>
    </div>
  );
}

function useOnline() {
  const [on, setOn] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const up = () => setOn(true);
    const down = () => setOn(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return on;
}

export function LogMeetingModal({ clientId, onClose, onSubmitted }: { clientId?: string; onClose: () => void; onSubmitted?: (id: string) => void }) {
  const { me, can } = useSession();
  const navigate = useNavigate();
  const clients = useClients();
  const privacy = usePrivacy();
  const { errs, setErrs, fail, toast } = useFail(SHOWN);
  const today = todayIso();
  const voice = useMemo(() => probe() === 'supported', []);
  const online = useOnline();
  const keyFor = useRequestKey();

  // A member logs meetings for the clients they are SPOC of (the server refuses others).
  const options = useMemo(() => {
    const all = clients.data ?? [];
    if (can('clients.read.all')) return all;
    const mine = all.filter((c) => c.spoc.id === me.user.id);
    return mine.length ? mine : all;
  }, [clients.data, can, me.user.id]);
  const [cid, setCid] = useState(clientId ?? '');
  useEffect(() => {
    if (!cid && options.length) setCid(options[0]!.id);
  }, [cid, options]);
  const client = options.find((c) => c.id === cid) ?? null;

  const [when, setWhen] = useState(0);
  const [title, setTitle] = useState('');
  const [attendees, setAttendees] = useState('');
  const [way, setWay] = useState<Way | null>(null);
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [showLink, setShowLink] = useState(false);
  const [consent, setConsent] = useState(false);
  const requireConsent = !!privacy.data?.recordings.require_consent;

  // ---------------------------------------------------------------- live recording
  const [rec, setRec] = useState<Rec>('idle');
  const [usedMs, setUsedMs] = useState(0);
  const [segMs, setSegMs] = useState(0);
  const [interim, setInterim] = useState('');
  const [why, setWhy] = useState<StopReason | null>(null);
  const [ask, setAsk] = useState<null | 'close' | Way>(null);
  const session = useRef<LiveSession | null>(null);
  const recording = rec === 'live' || rec === 'starting';
  const level = useMicLevel(rec === 'live');
  const totalS = Math.min(CAP_S, Math.floor((usedMs + segMs) / 1000));
  const leftS = CAP_S - totalS;
  const usedS = Math.min(CAP_S, Math.round(usedMs / 1000));

  useEffect(() => () => session.current?.stop(), []);

  // New words are appended at the END of the editor. Two things must hold while someone reads or fixes the text:
  // the box keeps showing the newest line when it was scrolled to the bottom (focused or not), and a caret placed
  // earlier in the text stays where it is (React writing the new value would otherwise throw it to the end).
  const ta = useRef<HTMLTextAreaElement | null>(null);
  const follow = useRef<{ stick: boolean; sel: [number, number] | null } | null>(null);
  const onFinal = (t: string) => {
    const el = ta.current;
    if (el) {
      const atEnd = el.selectionEnd >= el.value.length;
      follow.current = {
        stick: el.scrollHeight - el.scrollTop - el.clientHeight < 32,
        sel: document.activeElement === el && !atEnd ? [el.selectionStart, el.selectionEnd] : null,
      };
    }
    setText((x) => appendWords(x, t));
  };
  useLayoutEffect(() => {
    const el = ta.current;
    const f = follow.current;
    if (!el || !f) return;
    follow.current = null;
    if (f.sel && document.activeElement === el) el.setSelectionRange(f.sel[0], f.sel[1]);
    if (f.stick) el.scrollTop = el.scrollHeight;
  }, [text]);

  const record = () => {
    if (!voice || recording) return;
    if (privacy.isPending) return toast('Loading the recording settings. Try again in a moment.');
    if (privacy.isError) {
      void privacy.refetch();
      return toast('The recording settings did not load. Try again.');
    }
    if (requireConsent && !consent) return setErrs({ consent_confirmed: 'Tick this first, so nobody is recorded without knowing.' });
    const budget = CAP_MS - usedMs;
    if (budget < 1000) return;
    setErrs({});
    setWhy(null);
    setSegMs(0);
    setRec('starting');
    try {
      session.current = startLive({
        budgetMs: budget,
        onStart: () => setRec('live'),
        onFinal,
        onInterim: setInterim,
        onTick: setSegMs,
        onEnd: (reason, used) => {
          session.current = null;
          setUsedMs((u) => Math.min(CAP_MS, u + used));
          setSegMs(0);
          setInterim('');
          setRec('stopped');
          setWhy(reason);
          setAsk(null);
        },
      });
    } catch {
      setRec('stopped');
      setWhy('error');
    }
  };
  const stopRec = () => session.current?.stop();

  /** Closing, or switching ways, while recording asks first (design 10). Nothing runs in the background. */
  const guardedClose = () => (recording ? setAsk('close') : onClose());
  const pickWay = (w: Way) => {
    if (w === way) return;
    if (w === 'live' && !voice) return;
    if (recording) return setAsk(w);
    setErrs({});
    setWay(w);
  };

  // ---------------------------------------------------------------- document
  const [doc, setDoc] = useState<Doc>({ k: 'none' });
  const fileIn = useRef<HTMLInputElement>(null);
  const lastDocText = useRef('');
  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    if (doc.k === 'reading') doc.ctl.abort();
    const e = extOf(file.name);
    if (!(MINUTES_FILE_EXTENSIONS as readonly string[]).includes(`.${e}`)) {
      const msg = `This file type can’t be read. Upload the minutes as a PDF, Word (.docx), Excel (.xlsx), CSV or text file.${
        AUDIO.test(e) ? ' Audio files aren’t accepted: record live instead, or type the minutes.' : ''
      }`;
      return setDoc({ k: 'bad', file, msg, server: false });
    }
    if (file.size > MINUTES_FILE_MAX_BYTES) {
      return setDoc({ k: 'bad', file, msg: `This file is ${(file.size / MB).toFixed(1)} MB. Files can be up to ${CAP_MB} MB. Save it without the images, or paste only the minutes.`, server: false });
    }
    const ctl = new AbortController();
    setDoc({ k: 'reading', file, ctl });
    try {
      const r = await readMeetingDocument(file, ctl.signal);
      if (ctl.signal.aborted) return;
      setText((t) => (!t.trim() || t === lastDocText.current ? r.text : `${t.trimEnd()}\n\n${r.text}`));
      lastDocText.current = r.text;
      setDoc({ k: 'ok', file, filename: r.filename, chars: r.chars });
    } catch (err) {
      if (ctl.signal.aborted) return;
      const msg = err instanceof ApiFailure && err.status === 422 ? (err.fieldErrors.file ?? errorText(err)) : errorText(err);
      setDoc({ k: 'bad', file, msg, server: true });
    }
  };

  // ---------------------------------------------------------------- submit
  const n = text.trim().length;
  const save = useMeetingWrite((body: CreateMeetingBody) => call('meetings.create', { idempotencyKey: keyFor(body), body }));
  const bodyFor = (): CreateMeetingBody | string => {
    if (!cid) return 'Pick the client';
    const t = title.trim();
    if (t.length < 3) {
      setErrs({ title: t ? 'Add a meeting title of at least 3 characters' : 'Add a meeting title' });
      return '';
    }
    if (t.length > 160) {
      setErrs({ title: 'Keep the title under 160 characters' });
      return '';
    }
    if (attendees.trim().length > 500) {
      setErrs({ attendees: 'Keep who was there under 500 characters' });
      return '';
    }
    const common = { client_id: cid, held_on: addDays(today, when), title: t, attendees: attendees.trim() || undefined };
    const url = link.trim();
    if (way === 'link') {
      if (!/^https:\/\/\S+$/i.test(url)) {
        setErrs({ recording_url: 'Add an https link to the recording' });
        return '';
      }
      return { ...common, media_kind: 'recorded', recording_url: url };
    }
    if (n < MIN_CHARS) {
      setErrs({ minutes_text: 'Add a little more: minutes need at least 10 characters.' });
      return '';
    }
    if (n > MAX_CHARS) {
      setErrs({ minutes_text: 'The minutes are over 100,000 characters. Keep only the minutes.' });
      return '';
    }
    const minutes_text = text.trim();
    if (way === 'doc') {
      if (doc.k !== 'ok') return 'Choose the minutes file';
      return { ...common, media_kind: 'upload', transcript_source: 'document', document_filename: doc.filename, minutes_text };
    }
    if (way === 'live' && usedMs > 0) {
      if (showLink && url && !/^https:\/\/\S+$/i.test(url)) {
        setErrs({ recording_url: 'Add an https link to the recording' });
        return '';
      }
      return {
        ...common,
        media_kind: 'recorded',
        transcript_source: 'live',
        minutes_text,
        duration_s: Math.min(CAP_S, usedS),
        consent_confirmed: requireConsent ? consent : undefined,
        recording_url: showLink && url ? url : undefined,
      };
    }
    // Typed, pasted, or a live way where nothing was recorded (the microphone was blocked): typed minutes.
    return { ...common, media_kind: 'typed', minutes_text };
  };
  const submit = () => {
    setErrs({});
    const b = bodyFor();
    if (typeof b === 'string') return b ? toast(b) : undefined;
    save.mutate(b, {
      onSuccess: (m: Meeting) => {
        markJustAddedMeeting(m.id);
        toast(way === 'link' ? 'Added. The recording is stored as a link only.' : 'Minutes submitted. Finding the tasks now.');
        onClose();
        if (onSubmitted) onSubmitted(m.id);
        else navigate(`/clients/meetings?meeting=${m.id}`);
      },
      onError: fail,
    });
  };

  // ---------------------------------------------------------------- what the footer says, and whether Submit is on
  const tooShort = way === 'live' && rec === 'stopped' && why !== 'blocked' && n < MIN_CHARS;
  const ready =
    online &&
    !save.isPending &&
    (way === 'link'
      ? !!link.trim()
      : way === 'doc'
        ? doc.k === 'ok' && n >= MIN_CHARS
        : way === 'live'
          ? !recording && n >= MIN_CHARS
          : way === 'type' && n >= MIN_CHARS);
  const status: ReactNode = !way
    ? voice
      ? 'Pick one. You can switch before you submit.'
      : ''
    : !online
      ? (
          <>
            <Icon name="wifioff" size={13} /> Offline: Submit comes back when you reconnect. Your text stays here.
          </>
        )
      : save.isPending
        ? 'Submitting…'
        : recording
          ? 'Stop the recording to submit. You can fix the text while it records.'
          : way === 'doc' && doc.k === 'reading'
            ? 'Reading the document…'
            : (way === 'live' && rec === 'stopped' && !tooShort && why !== 'blocked') || (way === 'doc' && doc.k === 'ok')
              ? 'Next: the tasks and decisions are found for you to check.'
              : '';

  // ---------------------------------------------------------------- parts
  const whenLabel = WHEN_OPTS.find(([, d]) => d === when)?.[0] ?? '';
  const clientSel = (
    <select className="in" value={cid} onChange={(e) => setCid(e.target.value)} disabled={clients.isPending || recording} aria-label="Client">
      {clients.isPending ? <option>Loading…</option> : null}
      {options.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
    </select>
  );
  const whenSel = (
    <select className="in" value={when} onChange={(e) => setWhen(Number(e.target.value))} aria-label="When">
      {WHEN_OPTS.map(([l, d]) => (
        <option key={l} value={d}>
          {l}
        </option>
      ))}
    </select>
  );
  const titleIn = <input className="in" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Weekly sync with the client" aria-label="Meeting title" />;
  const whoIn = (
    <input className="in" value={attendees} onChange={(e) => setAttendees(e.target.value)} placeholder="e.g. the client's finance lead, the account manager" aria-label="Who was there" />
  );
  const fld = (label: string, input: ReactNode, err?: string, cls?: string) => (
    <label className={`fld${cls ? ` ${cls}` : ''}`}>
      <span>{label}</span>
      {input}
      {err ? <small className="ferr">{err}</small> : null}
    </label>
  );

  const details = !way ? (
    <>
      <div className="cols c2">
        {fld('Client', clientSel, errs.client_id)}
        {fld('When', whenSel, errs.held_on)}
      </div>
      {fld('Meeting title', titleIn, errs.title)}
      {fld('Who was there', whoIn, errs.attendees)}
    </>
  ) : (
    <>
      <div className="mv-sum">
        <Icon name="mic" size={14} />
        <span>
          <b>{client?.name ?? ''}</b> · {whenLabel}
          {title.trim() ? ` · ${title.trim()}` : ''}
        </span>
        <span className="footnote">Stop to change</span>
      </div>
      <div className="mdet">
        {fld('Client', clientSel, errs.client_id)}
        {fld('When', whenSel, errs.held_on)}
        {fld('Meeting title', titleIn, errs.title, 'wide')}
        {fld('Who was there', whoIn, errs.attendees, 'wide')}
      </div>
    </>
  );

  const tiles = (
    <>
      <div className="lbl" style={{ marginBottom: 8 }}>
        How do you want to add the minutes?
      </div>
      <div className="ways">
        {WAYS.map(([k, i, t, d, tag]) => {
          const off = k === 'live' && !voice;
          return (
            <button key={k} type="button" className="way" aria-disabled={off || undefined} onClick={() => !off && pickWay(k)}>
              <span className="wi">
                <Icon name={off ? 'micoff' : i} />
              </span>
              <span>
                <b>{t}</b>
                <span className="wd">{d}</span>
                {tag ? <span className={`chip${off ? ' cold' : ''}`}>{off ? 'Not available in this browser' : tag}</span> : null}
              </span>
            </button>
          );
        })}
      </div>
      {!voice ? (
        <div className="note warn" style={{ marginTop: 14 }}>
          <Icon name="micoff" size={15} /> <b>Voice typing is not available in this browser. Type it instead.</b>
          <br />
          You can also paste the minutes or upload them as a document.
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn sm pri" onClick={() => pickWay('type')}>
              <Icon name="pen" size={14} /> Type the minutes instead
            </button>
            <button className="btn sm" onClick={() => pickWay('doc')}>
              <Icon name="doc" size={14} /> Upload a document
            </button>
          </div>
        </div>
      ) : null}
    </>
  );

  const seg = (
    <div className="mv-way">
      <div className="lbl" style={{ marginBottom: 6 }}>
        Minutes
      </div>
      <div style={{ marginBottom: 14 }}>
        <div className="seg ways-seg">
          {(Object.keys(SEGL) as Way[]).map((k) => (
            <button
              key={k}
              type="button"
              className={k === way ? 'on' : ''}
              aria-pressed={k === way}
              disabled={k === 'live' && !voice}
              title={k === 'live' && !voice ? 'Not available in this browser' : undefined}
              onClick={() => pickWay(k)}
            >
              <Icon name={SEGI[k]} />
              {SEGL[k]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const editor = (o: { label: string; meta: string; rows?: number; bad?: boolean; ph?: string; hearing?: boolean; aria?: string }) => (
    <>
      <div className="txhead">
        <span className="lbl">{o.label}</span>
        <div className="grow" />
        <span className={`cnt${o.bad ? ' bad' : ''}`}>{o.meta}</span>
      </div>
      <textarea
        className="in"
        rows={o.rows ?? 6}
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label={o.aria ?? 'Transcript'}
        aria-invalid={o.bad || !!errs.minutes_text || undefined}
        style={o.bad ? { borderColor: 'var(--cold-line)' } : undefined}
        placeholder={o.ph ?? 'Words appear here as you speak. You can also type.'}
        ref={ta}
      />
      {o.hearing ? (
        <div className="hearing" aria-live="polite">
          <span className="rdot" />
          Hearing: {interim.length > HEAR_MAX ? `…${interim.slice(-HEAR_MAX).replace(/^\S*\s+/, '')}` : interim}
          <span className="caret" />
        </div>
      ) : null}
      {errs.minutes_text ? (
        <small className="ferr2" style={{ marginTop: 6 }}>
          {errs.minutes_text}
        </small>
      ) : null}
    </>
  );

  const ring = (left: number) => {
    const r = 36;
    const c = 2 * Math.PI * r;
    const warn = left <= 30 && left < CAP_S;
    return (
      <div className={`mv-ring${warn ? ' warn' : ''}`} role="timer" aria-label={`${mmss(left)} left`}>
        <svg viewBox="0 0 84 84">
          <circle className="mv-tr" cx="42" cy="42" r={r} />
          <circle className="mv-pr" cx="42" cy="42" r={r} strokeDasharray={c} strokeDashoffset={c * (1 - left / CAP_S)} />
        </svg>
        <div className="mv-rv">
          <b>{mmss(left)}</b>
          <small>{left === CAP_S ? 'max' : 'left'}</small>
        </div>
      </div>
    );
  };

  const recCard = (state: 'idle' | 'live' | 'off', head: ReactNode, meta: ReactNode, notice = true) => (
    <div className={`mv-rec${state === 'live' ? ' live' : ''}`}>
      {state === 'live' ? (
        <button className="mv-rbtn stop" aria-label="Stop recording" onClick={stopRec}>
          <i />
        </button>
      ) : (
        <button className="mv-rbtn" aria-label="Record" disabled={state === 'off' || rec === 'starting'} onClick={record}>
          <Icon name="mic" />
        </button>
      )}
      <div className="mv-rb">
        <div className="mv-rt">
          {state === 'live' ? <span className="rdot" /> : null}
          {head}
          {state === 'live' ? <span className="elapsed">{mmss(totalS)}</span> : null}
        </div>
        <div className="mv-rm">{meta}</div>
        {state === 'off' ? null : (
          <div className={`lvl ${state === 'live' ? 'on' : 'off'}`} aria-hidden="true">
            {level.map((v, i) => (
              <i key={i} style={{ height: state === 'live' ? 6 + Math.round(v * 24) : 4 }} />
            ))}
          </div>
        )}
        {notice ? (
          <div className="vnotice">
            <Icon name="alert" size={14} />
            <span>{VENDOR_NOTICE}</span>
          </div>
        ) : null}
      </div>
      {state === 'off' ? null : ring(leftS)}
    </div>
  );

  const recordMore = (pri = false) => (
    <button className={`btn sm${pri ? ' pri' : ''}`} onClick={record} disabled={leftS < 1 || !online}>
      <Icon name="mic" size={14} /> {leftS < 1 ? 'No recording time left' : `Record more · ${mmss(leftS)} left`}
    </button>
  );

  const stoppedNote = (): ReactNode => {
    if (tooShort) {
      return (
        <div className="note warn" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Icon name="alert" size={18} />
          <div>
            <b>Too little was heard to make minutes.</b> Minutes need at least 10 characters, and this has {n}. Check the microphone is close and not muted,
            then record more, or type the rest.
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>{recordMore(true)}</div>
          </div>
        </div>
      );
    }
    const box = (cls: string, icon: IconName, body: ReactNode) => (
      <div className={`note ${cls}`} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Icon name={icon} size={18} />
        <div>{body}</div>
      </div>
    );
    switch (why) {
      case 'cap':
        return box('warn', 'alert', <><b>That’s the 2-minute limit.</b> Edit the text, or add the rest by typing.</>);
      case 'network':
        return box(
          'warn',
          'wifioff',
          <>
            <b>Voice typing lost its connection. Your text so far is below. Record again or type.</b>
            <br />
            The speech service needs the internet, so the recording stopped at {mmss(usedS)}. Nothing you said before that was lost. Record more and Submit come back when
            you are online.
          </>,
        );
      case 'nothing':
        return box('warn', 'alert', <><b>Nothing was recognised.</b> Check your microphone and try again.</>);
      case 'restarts':
      case 'error':
        return box('warn', 'alert', <><b>Voice typing stopped. Your text so far is below.</b> Record more, or type the rest.</>);
      default:
        return box('good', 'check', <><b>Recording stopped.</b> Read it through and fix names, numbers and dates before you submit. Nothing has been saved yet.</>);
    }
  };

  const consentTick = requireConsent ? (
    <>
      <label className="consent">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>{privacy.data!.recordings.consent_text}</span>
      </label>
      {errs.consent_confirmed ? <small className="ferr2">{errs.consent_confirmed}</small> : null}
      <div style={{ height: 10 }} />
    </>
  ) : null;

  let body: ReactNode = null;
  if (way === 'live') {
    if (why === 'blocked' && !recording) {
      body = (
        <>
          <MicBlockedNote onRetry={record} onUpload={() => pickWay('doc')} />
          {recCard('off', <span style={{ color: 'var(--mute)' }}>Microphone blocked</span>, 'Type or paste the minutes below. They are submitted the same way.', false)}
          {editor({ label: 'Minutes', meta: `${n} characters`, aria: 'Minutes', ph: 'What was discussed, what was decided, and who will do what by when.' })}
        </>
      );
    } else if (recording) {
      const last = leftS <= 30;
      body = (
        <>
          {ask ? (
            <div className="inline-confirm" role="alertdialog" aria-label="Stop recording?">
              <b>Stop recording? The text so far stays in the editor.</b>
              <button className="btn sm" onClick={() => setAsk(null)}>
                Keep recording
              </button>
              <button
                className="btn sm pri"
                onClick={() => {
                  const next = ask;
                  setAsk(null);
                  stopRec();
                  if (next !== 'close') setWay(next);
                }}
              >
                Stop
              </button>
            </div>
          ) : null}
          {recCard(
            'live',
            rec === 'starting' ? 'Starting…' : 'Recording',
            last ? (
              <>
                <b style={{ color: '#B45309' }}>Stops at 2:00: {leftS} seconds left.</b> Finish the sentence. You can type the rest afterwards.
              </>
            ) : (
              'Speak normally. Press stop when you are done, or it stops at 2:00.'
            ),
            !ask,
          )}
          {editor({ label: 'Transcript · you can edit it, even while recording', meta: `${text.length} characters`, hearing: true })}
        </>
      );
    } else if (rec === 'stopped') {
      body = (
        <>
          {stoppedNote()}
          {editor({
            label: 'Transcript · you can edit it',
            meta: tooShort ? `${n} characters · ${MIN_CHARS} needed` : `${text.length} characters · ${mmss(usedS)} recorded`,
            rows: tooShort ? 3 : 9,
            bad: tooShort,
          })}
          {tooShort ? (
            errs.minutes_text ? null : (
              <small className="ferr2" style={{ marginTop: 6 }}>
                Add a little more: minutes need at least 10 characters.
              </small>
            )
          ) : (
            <>
              <div className="split" style={{ marginTop: 10, flexWrap: 'wrap' }}>
                {recordMore()}
                {!online ? <span className="footnote">Waiting for the connection…</span> : null}
                {!showLink ? (
                  <button className="lnk" style={{ fontSize: 12.5 }} onClick={() => setShowLink(true)}>
                    + Add a link to the full recording (optional)
                  </button>
                ) : null}
                <div className="grow" />
                {requireConsent && consent ? (
                  <span className="footnote">
                    <Icon name="check" size={13} /> Consent ticked
                  </span>
                ) : null}
              </div>
              {showLink ? (
                <div style={{ marginTop: 10 }}>
                  {fld(
                    'Link to the full recording (optional)',
                    <input className="in" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://" aria-label="Recording link" />,
                    errs.recording_url,
                  )}
                </div>
              ) : null}
            </>
          )}
        </>
      );
    } else {
      body = (
        <>
          {recCard('idle', 'Ready to record', 'Press the microphone and speak. Up to 2:00 for this meeting. Your browser asks to use the microphone the first time.')}
          {consentTick}
          {editor({ label: 'Transcript · you can edit it, even while recording', meta: text ? `${text.length} characters` : `${mmss(leftS)} of recording left` })}
        </>
      );
    }
  } else if (way === 'doc') {
    const chip = (cls: string, f: File, meta: string, right: ReactNode) => (
      <div className={`fchip${cls ? ` ${cls}` : ''}`}>
        <span className="fi">{extOf(f.name).toUpperCase() || 'FILE'}</span>
        <div className="fb">
          <div className="fn" title={f.name}>
            {f.name}
          </div>
          <div className="fm">{meta}</div>
        </div>
        {right}
      </div>
    );
    const input = (
      <input
        ref={fileIn}
        type="file"
        hidden
        accept={MINUTES_FILE_EXTENSIONS.join(',')}
        aria-label="Minutes file"
        onChange={(e) => {
          void pickFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    );
    const drop = (handlers: object, small: boolean, bad: boolean) =>
      small ? (
        <div className={`drop${bad ? ' bad' : ''}`} style={{ padding: '16px 20px' }} {...handlers}>
          <button className="btn sm" onClick={() => fileIn.current?.click()}>
            <Icon name="upload" size={14} /> Choose another file
          </button>{' '}
          <span className="footnote" style={{ marginLeft: 8 }}>
            PDF, Word, Excel, CSV or text · up to {CAP_MB} MB
          </span>
        </div>
      ) : (
        <div className="drop" {...handlers}>
          <span className="di">
            <Icon name="upload" />
          </span>
          <b>Drop the minutes file here</b>
          <div className="dd">or</div>
          <button className="btn" onClick={() => fileIn.current?.click()}>
            <Icon name="doc" size={15} /> Choose a file
          </button>
          <div className="types">
            {TYPES.map((t) => (
              <span key={t} className="chip">
                {t}
              </span>
            ))}
            <span className="chip brand">Up to {CAP_MB} MB</span>
          </div>
        </div>
      );
    const dnd = {
      onDragOver: (e: DragEvent) => e.preventDefault(),
      onDrop: (e: DragEvent) => {
        e.preventDefault();
        void pickFile(e.dataTransfer.files?.[0]);
      },
    };
    const remove = (
      <button className="btn sm ghost" aria-label="Remove" onClick={() => setDoc({ k: 'none' })}>
        ✕
      </button>
    );
    body =
      doc.k === 'none' ? (
        <>
          {input}
          {drop(dnd, false, false)}
          <div className="note mb0">
            The text is read and shown here so you can correct it before you submit. A table with columns like <b>Action</b>, <b>Owner</b> and <b>Due</b> turns into tasks
            best. Scanned PDFs (photos of pages) can’t be read: type or paste those instead.
          </div>
        </>
      ) : doc.k === 'bad' ? (
        <>
          {input}
          {chip('bad', doc.file, `${kindLabel(doc.file)} · ${sizeLabel(doc.file.size)}${doc.server ? ' · read on our server' : ''}`, remove)}
          <small className="ferr2" role="alert">
            {doc.msg}
          </small>
          {doc.server ? (
            <div className="split" style={{ margin: '-4px 0 14px', gap: 8 }}>
              <button className="btn sm pri" onClick={() => pickWay('type')}>
                <Icon name="pen" size={14} /> Type or paste instead
              </button>
            </div>
          ) : null}
          {drop(dnd, true, true)}
        </>
      ) : doc.k === 'reading' ? (
        <>
          {input}
          {chip(
            '',
            doc.file,
            `${kindLabel(doc.file)} · ${sizeLabel(doc.file.size)}`,
            <>
              <span className="spin" aria-hidden="true" />
              <span className="sub" style={{ fontSize: 12.5 }}>
                Reading the text…
              </span>
              <button
                className="btn sm ghost"
                onClick={() => {
                  doc.ctl.abort();
                  setDoc({ k: 'none' });
                }}
              >
                Cancel
              </button>
            </>,
          )}
          <div className="txhead" style={{ marginTop: 12 }}>
            <span className="lbl">Text from the document</span>
          </div>
          <div className="skl" aria-busy="true">
            {[96, 88, 92, 60, 0, 84, 70, 78].map((w, i) => (w ? <div key={i} className="skel" style={{ width: `${w}%` }} /> : <div key={i} style={{ height: 4 }} />))}
          </div>
        </>
      ) : (
        <>
          {input}
          {chip(
            'ok',
            doc.file,
            `${kindLabel(doc.file)} · ${sizeLabel(doc.file.size)} · ${doc.chars.toLocaleString('en-IN')} characters read`,
            <button className="btn sm" onClick={() => fileIn.current?.click()}>
              Replace file
            </button>,
          )}
          <div className="txhead" style={{ marginTop: 12 }}>
            <span className="lbl">Text from the document · you can edit it</span>
            <div className="grow" />
            <span className={`cnt${n < MIN_CHARS ? ' bad' : ''}`}>{text.length} characters</span>
          </div>
          <textarea className="in" rows={11} value={text} onChange={(e) => setText(e.target.value)} aria-label="Text from the document" style={{ fontSize: 13 }} />
          {errs.minutes_text ? <small className="ferr2">{errs.minutes_text}</small> : null}
          <div className="footnote" style={{ marginTop: 6 }}>
            Only the text is kept; the file is not saved.
          </div>
        </>
      );
  } else if (way === 'type') {
    body = (
      <>
        <textarea
          className="in"
          rows={9}
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label="Minutes"
          placeholder="What was discussed, what was decided, and who will do what by when."
        />
        {errs.minutes_text ? <small className="ferr2">{errs.minutes_text}</small> : null}
        <div className="split" style={{ marginTop: 8 }}>
          <span className="cnt">{text.length} characters</span>
          <div className="grow" />
          <span className="footnote">The tasks and decisions are found after you submit.</span>
        </div>
      </>
    );
  } else if (way === 'link') {
    body = (
      <>
        <label className="fld">
          <span>Recording link</span>
          <input className="in" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://" aria-label="Recording link" />
          {errs.recording_url ? (
            <small className="ferr">{errs.recording_url}</small>
          ) : (
            <small className="fhint">An https link to a recording held in Zoom, Google Meet, Drive or similar. Anyone opening it needs access there.</small>
          )}
        </label>
        <div className="note mb0">
          The recording is stored as a link only. Our system does not hold a copy and nothing is transcribed, so no tasks are found for you. After you submit, write
          the summary and add the tasks in the meeting’s review.
        </div>
      </>
    );
  }

  return (
    <Modal
      wide
      title="Log a meeting"
      sub="Add the minutes any way you like. Actions in them become tasks for the client’s SPOC."
      onClose={guardedClose}
      bodyClass={recording ? 'mv-recording' : undefined}
      footer={
        <>
          {status ? (
            <span className="sub" style={{ fontSize: 12.5 }}>
              {status}
            </span>
          ) : null}
          <div className="grow" />
          <button className="btn" onClick={guardedClose}>
            Cancel
          </button>
          {way ? (
            <button className="btn pri" onClick={submit} disabled={!ready}>
              {save.isPending ? 'Submitting…' : 'Submit minutes'}
            </button>
          ) : null}
        </>
      }
    >
      {details}
      {way ? seg : tiles}
      {body}
    </Modal>
  );
}
